import React, { useEffect, useRef, useState } from "react";
import { ConfirmActionDialog } from "src/components/modals/confirm-action-dialog";

export interface ConfirmableActionProps {
    title: string;
    body: string;
    confirmLabel: string;
    keepLabel: string;
    /** The actual destructive operation; awaited before the prompt closes. */
    onConfirm: () => void | Promise<unknown>;
    /** Disables the confirm button while the operation is in flight. */
    submitting?: boolean;
    confirmId?: string;
    keepId?: string;
    /**
     * Renders the normal (non-confirming) content. Wire a trigger's onClick to the provided
     * `requestConfirm` to swap this content for the confirmation prompt in place.
     */
    children: (requestConfirm: () => void) => React.ReactNode;
}

/**
 * Owns the ephemeral "am I confirming?" state for a destructive action and swaps its rendered
 * content between the caller's normal trigger(s) and a {@link ConfirmActionDialog}. The toggle
 * state lives here — colocated with the confirmation UI — rather than being lifted into a parent
 * component or a data hook. Give it a `key` that changes when the surrounding modal reopens if you
 * need the prompt to reset between sessions.
 */
export const ConfirmableAction = (props: ConfirmableActionProps): JSX.Element => {
    const [confirming, setConfirming] = useState(false);
    // Avoid a setState-after-unmount warning: a successful confirm often changes the parent's
    // state, unmounting this component before the awaited onConfirm resolves.
    const mountedRef = useRef(true);
    useEffect(() => () => { mountedRef.current = false; }, []);

    if (confirming) {
        return (
            <ConfirmActionDialog
                title={props.title}
                body={props.body}
                confirmLabel={props.confirmLabel}
                keepLabel={props.keepLabel}
                confirmId={props.confirmId}
                keepId={props.keepId}
                submitting={props.submitting}
                onConfirm={async () => {
                    // finally (not sequential code) so a rejecting onConfirm still clears the
                    // confirming state instead of wedging the dialog open. The rejection itself
                    // propagates — callers own error surfacing.
                    try {
                        await props.onConfirm();
                    } finally {
                        if (mountedRef.current) {
                            setConfirming(false);
                        }
                    }
                }}
                onKeep={() => setConfirming(false)}
            />
        );
    }
    return <>{props.children(() => setConfirming(true))}</>;
};
